import { formatTime } from "@xola/ui-kit";
import { DateFormat, formatDate, now } from "common";
import pluralize from "pluralize";
import React, { ReactNode } from "react";
import { BaseScheduleRepeat, BaseScheduleType, Schedule, ScheduleDeparture } from "types";
import { NoTranslate } from "../components/Common/NoTranslate";

const getSummaryBasedOnRepeat = (schedule: Schedule, timezone?: boolean) => {
    let summary = "";

    if (schedule.repeat === BaseScheduleRepeat.WEEKLY) {
        // Repeat weekly
        summary = schedule.type === BaseScheduleType.UNAVAILABLE ? "Blackout every " : "Every ";
        // Days
        if (schedule.days) {
            if (schedule.days.length === 7) {
                // All days selected
                summary = schedule.type === BaseScheduleType.UNAVAILABLE ? "Blackout daily" : "Daily";
            } else {
                // Some days selected
                /** Note: We need to use localized week day name for summary, so we are using dayjs.format rather than using {@link Weekday} */
                const days: string[] = schedule.days.map((day) => formatDate(now().set("day", day), DateFormat.DAY));
                summary += days.join(", ");
            }
        }
    } else if (schedule.dates && schedule.dates.length > 0) {
        // Custom dates with dates selected
        const maxDates = 4;
        let dates = schedule.dates?.slice();
        let extra = 0;

        const isSelectedDatesCountExceedMaxDatesCount = dates.length > maxDates;

        summary = schedule.type === BaseScheduleType.UNAVAILABLE ? "Blackout for trips departing on " : "Occurs on ";

        // There are too many dates to display in the summary, so show only the first few dates
        extra = isSelectedDatesCountExceedMaxDatesCount ? dates.length - maxDates : 0;

        // Loop through all dates and make them human readable
        dates = dates.slice(0, maxDates).map((date) => formatDate(date, DateFormat.DATE_LONG, timezone));

        summary += dates.join(", ");
        // Concatenate dates with comma delimiter
        if (isSelectedDatesCountExceedMaxDatesCount) {
            summary += ` ${extra || ""} + ${pluralize("more date", extra)}`;
        }
    } else if (schedule.type === BaseScheduleType.UNAVAILABLE) {
        summary = "Blackout custom dates (no dates selected)";
    } else {
        // Custom dates with no dates selected
        summary = "Custom dates (no dates selected)";
    }

    return summary;
};

export const getScheduleSummary = (schedule: Schedule, timezone?: boolean): ReactNode => {
    if (!schedule) {
        return "";
    }

    let summary = getSummaryBasedOnRepeat(schedule);
    if (schedule.departure === ScheduleDeparture.FIXED && schedule.times && schedule.times.length > 0) {
        const times: number[] = schedule.times?.map((time) => formatTime(time)) ?? [];
        summary += ` at ${times.join(", ")}`;
    }

    // Start and end date
    if (schedule.start && schedule.repeat !== BaseScheduleRepeat.CUSTOM) {
        summary += ` Starting on ${formatDate(schedule.start, DateFormat.DATE_LONG, timezone)}`;
    }

    if (schedule.end && schedule.repeat !== BaseScheduleRepeat.CUSTOM) {
        summary += ` until ${formatDate(schedule.end, DateFormat.DATE_LONG, timezone)}`;
    }

    if (schedule.timeRanges && schedule.timeRanges.length > 0) {
        const ranges = schedule.timeRanges.map((timeRange) => {
            return `${formatTime(timeRange.startTime)} - ${formatTime(timeRange.endTime)}`;
        });
        summary += ` between ${ranges.join(", ")}`;
    }

    return getSummaryWithNoTranslateTags(summary);
};

/** Wraps summary string variables to <NoTranslate></NoTranslate> tag.
 *
 * While creating the summary string, we are wrapping variables inside "{}",
 * and before rendering it we need to convert those "{}" to <NoTranslate /> tags,
 * so localize js would ignore that while translating the page content.
 *
 * @example
 * Summary String: "Starting on {21 May 2000 00:00:00}"
 * Result: Starting on <NoTranslate>21 May 2000 00:00:00</NoTranslate>
 **/
export const getSummaryWithNoTranslateTags = (summary: string) => {
    const REGEX = /{(.*?)}/g;
    const result: ReactNode[] = [];

    for (const [index, chunk] of summary.split(REGEX).entries()) {
        if (!isEven(index)) {
            /** Every even element (i.e. "ODD INDEX") of the array would be variable data */
            result.push(<NoTranslate>{chunk}</NoTranslate>);
            continue;
        }

        result.push(chunk);
    }

    return result;
};

export const isEven = (value: number) => value % 2 === 0;
